import { Select, Tooltip, Typography } from 'antd'
import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useResizeDetector } from 'react-resize-detector'
import styled from 'styled-components'

import { DocumentOntology } from './useDocumentOntologyAdditions'

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 100%;
  width: 100%;
  min-width: 0;
  flex: 1 1 auto;
`

const { Text } = Typography

const Label = styled(Text)`
  white-space: nowrap;
  flex-shrink: 0;
`

const SelectWrapper = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`

const StretchSpan = styled.span<{ $maxWidth?: string }>`
  display: block;
  width: 100%;
  max-width: ${({ $maxWidth }) => $maxWidth ?? '100%'};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

export const DocumentOntologySelector: FC<{
  label?: string
  isLoading: boolean
  documentOntologies: ReadonlyArray<DocumentOntology>
  selectedOntologyId: string | null
  setSelectedOntologyId: Dispatch<SetStateAction<string | null>>
}> = ({
  label = 'Document Type',
  documentOntologies,
  isLoading,
  setSelectedOntologyId,
  selectedOntologyId,
}) => {
  const options = useMemo(
    () =>
      documentOntologies
        .map((ontology) => ({
          label: ontology.name,
          value: ontology.id,
        }))
        .toSorted((a, b) => a.value.localeCompare(b.value)),
    [documentOntologies]
  )

  useEffect(() => {
    if (
      !options.map((option) => option.value).includes(selectedOntologyId ?? '')
    ) {
      setSelectedOntologyId(documentOntologies[0]?.id ?? null)
    }
  }, [documentOntologies, options, selectedOntologyId, setSelectedOntologyId])

  return (
    <Container>
      {label && <Label strong>{label}: </Label>}
      <SelectWrapper>
        <Select
          style={{ width: '100%' }}
          loading={isLoading}
          options={options}
          value={selectedOntologyId ?? undefined}
          onChange={(value) => setSelectedOntologyId(value ?? null)}
          optionRender={(option) => (
            <DropdownOptionLabel text={option.label as string} />
          )}
          labelRender={(option) => (
            <DropdownOptionLabel
              text={option.label as string}
              maxWidth='calc(100% - 24px)'
            />
          )}
        />
      </SelectWrapper>
    </Container>
  )
}

const useIsEllipsed = (text: string) => {
  const [isEllipsed, setIsEllipsed] = useState(false)
  const { ref } = useResizeDetector<HTMLElement>({
    refreshMode: 'debounce',
    refreshRate: 100,
  })

  useEffect(() => {
    if (!ref.current) return
    const element = ref.current
    setIsEllipsed(element.scrollWidth > element.clientWidth)
  }, [ref, text])

  return { ref, isEllipsed }
}

const DropdownOptionLabel: FC<{
  text: string
  maxWidth?: string
}> = ({ text, maxWidth }) => {
  const { ref, isEllipsed } = useIsEllipsed(text)

  return (
    <Tooltip title={isEllipsed ? text : undefined} mouseEnterDelay={0.5}>
      <StretchSpan ref={ref} $maxWidth={maxWidth}>
        {text}
      </StretchSpan>
    </Tooltip>
  )
}
